import React, { useEffect, useRef } from 'react';
import { drawBackdrop } from '../renderers/shared.js';

export function SceneCanvas({ runner, model, renderer, options, resetToken, onSnapshot, onFinish }) {
  const canvasRef = useRef(null);
  const trailRef = useRef([]);
  const lastTrailTRef = useRef(-1);
  const finishedNotifiedRef = useRef(false);
  const optionsRef = useRef(options);
  optionsRef.current = options;

  useEffect(() => {
    trailRef.current = [];
    lastTrailTRef.current = -1;
    finishedNotifiedRef.current = false;
  }, [runner, resetToken]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext('2d');
    let frameId = 0;
    let lastTime = performance.now();
    let lastEmit = 0;
    let width = 0;
    let height = 0;
    let dpr = 1;

    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      const nextDpr = Math.min(window.devicePixelRatio || 1, 2);
      const nextWidth = Math.max(1, Math.round(rect.width));
      const nextHeight = Math.max(1, Math.round(rect.height));
      if (nextWidth === width && nextHeight === height && nextDpr === dpr) return;
      const hadSize = width > 1 && height > 1;
      width = nextWidth;
      height = nextHeight;
      dpr = nextDpr;
      canvas.width = Math.round(width * dpr);
      canvas.height = Math.round(height * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      // 视口尺寸变化后旧轨迹坐标失效，避免课堂模式切换时出现拉伸伪影。
      if (hadSize) trailRef.current = [];
    };
    resize();
    const observer = new ResizeObserver(resize);
    observer.observe(canvas);

    const loop = (now) => {
      const dt = Math.min(0.06, Math.max(0, (now - lastTime) / 1000));
      lastTime = now;
      if (runner.running) {
        runner.advance(dt);
        if (runner.finished && !finishedNotifiedRef.current) {
          finishedNotifiedRef.current = true;
          onFinish?.();
        }
      }

      if (runner.state.t < lastTrailTRef.current - 0.05) {
        trailRef.current = [];
        finishedNotifiedRef.current = false;
      }
      lastTrailTRef.current = runner.state.t;

      if (optionsRef.current.trail && renderer.trailPoint) {
        const point = renderer.trailPoint({
          width,
          height,
          state: runner.state,
          params: runner.params,
        });
        if (point) {
          const trail = trailRef.current;
          const last = trail[trail.length - 1];
          if (!last || Math.hypot(point.x - last.x, point.y - last.y) > 2.2) {
            trail.push(point);
            if (trail.length > 84) trail.shift();
          }
        }
      } else if (trailRef.current.length) {
        trailRef.current = [];
      }

      drawBackdrop(ctx, width, height, {
        grid: optionsRef.current.grid,
        accent: model.meta.accent,
      });

      const metrics = model.measure(runner.state, runner.params);
      renderer.draw(ctx, {
        width,
        height,
        state: runner.state,
        params: runner.params,
        metrics,
        options: optionsRef.current,
        accent: model.meta.accent,
        trail: trailRef.current,
        time: runner.time,
      });

      if (now - lastEmit > 90 && runner.running) {
        lastEmit = now;
        onSnapshot?.(runner.getSnapshot());
      }

      frameId = requestAnimationFrame(loop);
    };

    frameId = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(frameId);
      observer.disconnect();
    };
  }, [model, onFinish, onSnapshot, renderer, runner]);

  return <canvas ref={canvasRef} className="scene-canvas" aria-label={`${model.meta.title}实时动力学可视化`} />;
}
